import { AfterViewInit, Directive, ElementRef, OnDestroy, inject } from "@angular/core";
import { MultiSelect } from "@openng/optimus-ui/multiselect";
import { Select } from "@openng/optimus-ui/select";
import { Subscription } from "rxjs";

@Directive({
    selector: "p-select[filter], p-multiSelect",
    standalone: false
})
export class FilterDropdownOnTypeDirective implements AfterViewInit, OnDestroy {
    private readonly elementRef = inject(ElementRef<HTMLElement>);
    private readonly select = inject(Select, { optional: true, self: true });
    private readonly multiSelect = inject(MultiSelect, { optional: true, self: true });
    private pendingFilter = "";
    private filterInput: HTMLInputElement | null = null;
    private showSubscription: Subscription;
    private hideSubscription: Subscription;

    private readonly handleKeyDown = (event: KeyboardEvent): void => {
        const dropdown = this.select ?? this.multiSelect;
        if (!dropdown?.filter || dropdown.readonly || dropdown.loading || this.select?.editable ||
            event.target !== dropdown.focusInputViewChild?.nativeElement || event.isComposing ||
            event.altKey || event.ctrlKey || event.metaKey || event.key.length !== 1 || event.key === " ") {
            return;
        }

        const currentFilter = dropdown.overlayVisible ? dropdown.filterValue ?? this.pendingFilter : "";
        this.pendingFilter = currentFilter + event.key;
        dropdown.filterValue = this.pendingFilter;
        if (!dropdown.overlayVisible) {
            dropdown.show();
        }

        event.preventDefault();
        event.stopImmediatePropagation();
    };

    private readonly handleFilterKeyDown = (event: KeyboardEvent): void => {
        const dropdown = this.select ?? this.multiSelect;
        if (event.key !== "Enter" || event.isComposing || event.altKey || event.ctrlKey || event.metaKey ||
            !dropdown?.filter || !dropdown.overlayVisible || !this.filterInput?.value.trim()) {
            return;
        }

        const options = (dropdown.visibleOptions() as unknown[]).filter(option => Boolean(dropdown.isValidOption(option)));
        if (options.length !== 1) {
            return;
        }

        if (this.select != null) {
            this.select.onOptionSelect(event, options[0]);
        }
        else if (this.multiSelect != null && !this.multiSelect.isSelected(options[0])) {
            this.multiSelect.onOptionSelect({ originalEvent: event, option: options[0] });
        }

        event.preventDefault();
        event.stopImmediatePropagation();
    };

    ngAfterViewInit(): void {
        this.elementRef.nativeElement.addEventListener("keydown", this.handleKeyDown, true);
        this.showSubscription = (this.select?.onShow ?? this.multiSelect?.onPanelShow)?.subscribe(() => this.attachFilterInput());
        this.hideSubscription = (this.select?.onHide ?? this.multiSelect?.onPanelHide)?.subscribe(() => this.detachFilterInput());
    }

    ngOnDestroy(): void {
        this.elementRef.nativeElement.removeEventListener("keydown", this.handleKeyDown, true);
        this.detachFilterInput();
        this.showSubscription?.unsubscribe();
        this.hideSubscription?.unsubscribe();
    }

    private attachFilterInput(): void {
        this.detachFilterInput();
        this.filterInput = (this.select?.filterViewChild ?? this.multiSelect?.filterInputChild)?.nativeElement as HTMLInputElement ?? null;
        this.filterInput?.addEventListener("keydown", this.handleFilterKeyDown, true);
    }

    private detachFilterInput(): void {
        this.filterInput?.removeEventListener("keydown", this.handleFilterKeyDown, true);
        this.filterInput = null;
    }
}
